import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomHeader } from '../../../shared/components/custom-datepicker-header/custom-datepicker-header.component';
import { AlertComponent } from '../../../shared/components/dialogs/alert/alert.component';
import { BudgetsService } from '../../services/budgets/budgets.service';

import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { BudgetsApprovalComponent } from '../../../shared/components/dialogs/budgets-approval/budgets-approval.component';
import { ConfirmDialogComponent } from '../../../shared/components/dialogs/confirm/confirm.component';
import { supplier } from '../../../suppliers/models/supplier';
import { SuppliersService } from '../../../suppliers/services/suppliers.service';
import { Budget } from '../../models/budget';
import { OrdersService } from '../../services/orders/orders.service';

@Component({
  selector: 'app-budgets-component',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatOptionModule,
    MatDialogModule,
    TranslateModule,
    FlexLayoutModule,
    MatTableModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatTooltip,

  ],
  standalone: true,
  templateUrl: './budgets-component.component.html',
  styleUrl: './budgets-component.component.scss',
})
export class BudgetsComponent implements OnInit {
  @Input() supplierIds: number[] = [];
  orderStatus!: string;
  @Output() supplierIdsChange = new EventEmitter<number[]>();

  @ViewChild('form') itemFormViewChild: any;

  customHeader = CustomHeader;
  suppliers: supplier[] = [];
  budgetForm: FormGroup;
  tableData = new MatTableDataSource<Budget>();

  showSelectColumn: boolean = true;
  @Output() budgetsPendingApprovalChange = new EventEmitter<boolean>();
  allUserLabels: any;
  selectionModel = new SelectionModel<any>(true, []);
  allSelected: boolean = false;
  budgetsReleasedForApproval: boolean = false;
  selectedRows: any[] = [];

  largestPaymentTerm: number | null = null;
  smallestPaymentTerm: number | null = null;

  largestDeliveryTerm: number | null = null;
  smallestDeliveryTerm: number | null = null;

  largestTotalValue: number | null = null;
  smallestTotalValue: number | null = null;

  hasInsufficientItems: boolean = false;

  displayedColumns: string[] = [
    'select',
    'warning',
    'supplier',
    'budgetCreationDate',
    'expectedReturn',
    'budgetResponse',
    'paymentTerm',
    'deliveryTerm',
    'totalValue',
    'status',
    'action',
  ];

  orderId: number;
  generalRemarks: string = '';
  projectDescription: string = '';
  projectType: string = '';


  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private ordersService: OrdersService,
    private BudgetsService: BudgetsService,
    private suppliersService: SuppliersService,
    public authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService
  ) {
    this.orderId = Number(this.route.snapshot.paramMap.get('id'));
    this.budgetForm = this.formBuilder.group({
      supplier: ['', Validators.required],
      expectedReturn: ['', Validators.required],
    });
  }

  ngOnInit() {
    this.loadOrderDetails();
    this.loadSuppliers();
    this.loadBudgets();

    this.selectionModel.changed.subscribe(() => {
        const selectedBudgets = this.selectionModel.selected;
        const supplierIds = selectedBudgets.map((budget: Budget) => budget.supplierId);
        this.supplierIdsChange.emit(supplierIds);
        this.supplierIds = supplierIds;

        this.allSelected = this.tableData.data.length > 0 &&
                          this.selectionModel.selected.length === this.tableData.data.length;
      });
  }

  hasVisibilityPermission(): boolean {
    return (
      this.authenticationService.isApproverBudgets ||
      this.authenticationService.isAdmin ||
      this.authenticationService.isApproverOrders
    );
  }

  shouldShowVisibilityButton(): boolean {
    const allowedStatuses = ["WAITING_BUDGET_APPROVAL", "FINISHED"];
    return allowedStatuses.includes(this.orderStatus);
  }

  shouldShowViewButton(): boolean {
    return this.shouldShowVisibilityButton() && this.hasVisibilityPermission();
  }

  shouldShowApprovalButton(): boolean {
    const isApproverOrAdmin = this.authenticationService.isApproverBudgets || this.authenticationService.isAdmin;
    return this.orderStatus === "WAITING_BUDGET_APPROVAL" && isApproverOrAdmin;
  }

  shouldShowEditAndDisableButtons(): boolean {
    return this.orderStatus === "WAITING_SUPPLIERS_RESPONSE" || this.orderStatus === "WAITING_BUDGET_REQUEST_SUBMISSION";
  }

  onBudgetApproved() {
    this.loadOrderDetails();
    this.loadBudgets();
  }

  loadOrderDetails() {
    this.ordersService
      .getOrderDetails(+this.orderId!)
      .subscribe((orderItems) => {
        this.projectDescription = orderItems[0].projectDescription;
        this.projectType = orderItems[0].projectType;
        this.generalRemarks = orderItems[0].generalRemarks;
        this.orderStatus = orderItems[0].status;
        if (this.orderStatus === "WAITING_BUDGET_APPROVAL") {
          this.budgetsReleasedForApproval = true;
        }
        this.updateSelectColumn();
      });
  }

  loadSuppliers(){
    this.suppliersService
      .getSuppliersByOrderId(+this.orderId!)
      .subscribe((data) => (this.suppliers = data));
  }

  private getMinAndMax(values: (number | null | undefined)[]): { min: number | null, max: number | null } {
    const filtered = values.filter((v): v is number => v != null);
    if (!filtered.length) {
      return { min: null, max: null };
    }
    return { min: Math.min(...filtered), max: Math.max(...filtered) };
  }

  loadBudgets() {
    if (this.orderId) {
      const budgetsObservable = this.authenticationService.isApproverBudgets
        ? this.ordersService.getReleasedBudgetsByOrder(+this.orderId)
        : this.ordersService.getAllBudgetsByOrder(+this.orderId);

      budgetsObservable.subscribe((data: Budget[]) => {
        if(data.some(item => item.itemsOvertaking > 0 )){
          this.hasInsufficientItems = true;
        }
        const paymentTerms = data.map(b => b.paymentTermDays);
        const deliveryTerms = data.map(b => b.deliveryTermDays);
        const totalValues = data.map(b => b.totalValue);

        const paymentRange = this.getMinAndMax(paymentTerms);
        this.largestPaymentTerm = paymentRange.max;
        this.smallestPaymentTerm = paymentRange.min;

        const deliveryRange = this.getMinAndMax(deliveryTerms);
        this.largestDeliveryTerm = deliveryRange.max;
        this.smallestDeliveryTerm = deliveryRange.min;

        const totalRange = this.getMinAndMax(totalValues);
        this.largestTotalValue = totalRange.max;
        this.smallestTotalValue = totalRange.min;

        data.forEach((budget: Budget) => {
          budget.status = this.translateStatus(budget.status);
        });

        this.tableData.data = data;
        this.updateSelectColumn();

        this.selectionModel.clear();
        for (const id of this.supplierIds) {
          const budget = data.find((b) => b.supplierId === id);
          if (budget) {
            this.selectionModel.select(budget);
          }
        }
      });
    }
  }

  checkStatus(status: string): boolean {
    return status !== 'WAITING_BUDGET_APPROVAL'
        && status !== 'FINISHED'
        && status !== 'REJECTED'
  }

  getPaymentTermClass(paymentTerm?: number): string {
    if (paymentTerm == null) return '';
    if (this.checkStatus(this.orderStatus)) return '';
    if (!this.authenticationService.isApproverBudgets && !this.authenticationService.isAdmin) return '';
    if (this.largestPaymentTerm != null && paymentTerm === this.largestPaymentTerm) {
        return 'color-green';
    } else if (this.smallestPaymentTerm != null && paymentTerm === this.smallestPaymentTerm) {
        return 'color-red';
    } else {
        return 'color-yellow';
    }
  }

  getDeliveryTermClass(deliveryTerm?: number): string {
    if (deliveryTerm == null) return '';
    if (this.checkStatus(this.orderStatus)) return '';
    if (!this.authenticationService.isApproverBudgets && !this.authenticationService.isAdmin) return '';
    if (this.smallestDeliveryTerm != null && deliveryTerm === this.smallestDeliveryTerm) {
        return 'color-green';
    } else if (this.largestDeliveryTerm != null && deliveryTerm === this.largestDeliveryTerm) {
        return 'color-red';
    } else {
        return 'color-yellow';
    }
  }

  getTotalValueClass(totalValue?: number): string {
    if (totalValue == null) return '';
    if (this.checkStatus(this.orderStatus)) return '';
    if (!this.authenticationService.isApproverBudgets && !this.authenticationService.isAdmin) return '';
    if (this.smallestTotalValue != null && totalValue === this.smallestTotalValue) {
        return 'color-green';
    } else if (this.largestTotalValue != null && totalValue === this.largestTotalValue) {
        return 'color-red';
    } else {
        return 'color-yellow';
    }
  }

  translateStatus(status: string): string {
    switch (status) {
      case 'WAITING_FOR_BUDGET':
        return this.translateService.instant('status.waiting-for-budget');
      case 'WAITING_FOR_APPROVAL':
        return this.translateService.instant('status.waiting-for-approval');
      case 'APPROVED':
        return this.translateService.instant('status.approved');
      case 'REJECTED':
        return this.translateService.instant('status.rejected');
      case 'NO_RESPONSE':
        return this.translateService.instant('status.no-response');
      case 'CANCELLED':
        return this.translateService.instant('status.cancelled');
      default:
        return status;
    }
  }

  hasBudgets(): boolean {
    return this.tableData.data.length > 0;
  }

  isAnyApprovedOrRejected(): boolean {
    return this.tableData.data.some(
      (budget) => budget.status === 'Aprovado' || budget.status === 'Reprovado'
    );
  }

  areAllBudgetsPendingApproval(): boolean {
    return this.tableData.data.every(
      (budget) =>
        budget.status === 'Aguardando Aprovação' || budget.status === 'Sem Retorno'
    );
  }

  hasAtLeastOneBudgetWaitingApproval(): boolean {
    return this.tableData.data.some(budget => budget.status === 'Aguardando Aprovação');
  }

  isApproverOrAdmin(): boolean {
    return (
      this.authenticationService.isApproverOrders ||
      this.authenticationService.isAdmin
    );
  }

  addSelectColumn(): void {
    if (!this.displayedColumns.includes('select')) {
      this.displayedColumns.unshift('select');
    }
    this.showSelectColumn = true;
  }

  removeSelectColumn(): void {
    this.displayedColumns = this.displayedColumns.filter(
      (column) => column !== 'select'
    );
    this.showSelectColumn = false;
  }

  shouldShowSelectColumn(): boolean {
    return (
      !this.isAnyApprovedOrRejected() &&
      !this.areAllBudgetsPendingApproval() &&
      this.isApproverOrAdmin()
    );
  }

  updateSelectColumn(): void {
    if (!this.hasBudgets()) {
      this.removeSelectColumn();
      return;
    }

    if (this.shouldShowSelectColumn()) {
      this.budgetsPendingApprovalChange.emit(false);
      this.addSelectColumn();
    } else {
      if (
        !this.authenticationService.isApproverBudgets &&
        this.hasBudgets() &&
        !this.budgetsReleasedForApproval &&
        this.orderStatus !== "FINISHED" &&
        this.hasAtLeastOneBudgetWaitingApproval()
      ) {
        this.budgetsPendingApprovalChange.emit(true);
      } else {
        this.budgetsPendingApprovalChange.emit(false);
      }
      this.removeSelectColumn();
    }
  }

  addSupplierIntoBudget() {
    if (this.budgetForm.valid && this.orderId) {
      const supplierId = this.budgetForm.get('supplier')?.value;
      const expectedReturnDate = this.budgetForm.get('expectedReturn')?.value;

      this.ordersService
        .addSupplierToOrder(+this.orderId, supplierId, expectedReturnDate)
        .subscribe({
          next: () => {
            const addedSupplier = this.suppliers.find((s) => s.id === supplierId);
            const supplierName = addedSupplier
              ? addedSupplier.corporateName
              : '';

            this.dialog.open(AlertComponent, {
              data: {
                title: 'alert.information',
                message: 'budget.supplierAddedSuccess',
                param: { supplierName }
              },
            });

            this.loadSuppliers();
            this.loadOrderDetails();
            this.loadBudgets();
            this.itemFormViewChild.resetForm();
          },
          error: () => {
            this.dialog.open(AlertComponent, {
              data: {
                title: 'alert.information',
                message: 'alert.supplierAlreadyExists',
              },
            });
            this.budgetForm
              .get('supplier')
              ?.setErrors({supplierExists: true});
          },
        });
    }
  }

  isIndeterminate() {
    const numSelected = this.selectionModel.selected.length;
    const numTotal = this.tableData.data.length;
    return numSelected > 0 && numSelected < numTotal;
  }

  toggleAll() {
    if (this.allSelected) {
      this.allSelected = false;
      this.selectionModel.clear();
      this.selectedRows = [];
    } else {
      this.allSelected = true;
      this.tableData.data.forEach((row) => this.selectionModel.select(row));
    }
  }

  navigateToBudgetInclusion(budget: any) {
    this.router.navigate(['/budget-inclusion', budget.id], {
      queryParams: {
        supplierCorporateName: budget.supplierCorporateName,
        projectType: this.projectType,
        projectDescription: this.projectDescription,
        orderId: this.orderId,
      }
    });
  }

  navigateToBudgetView(budget: any) {
    this.router.navigate(['/budget-inclusion', budget.id], {
      queryParams: {
        supplierCorporateName: budget.supplierCorporateName,
        orderId: this.orderId,
        projectType: this.projectType,
        projectDescription: this.projectDescription,
      }
    });
  }

  openBudgetApproval(budgetId: number) {
    this.BudgetsService.getBudgetDetails(budgetId).subscribe((budget) => {
      const dialogRef = this.dialog.open(BudgetsApprovalComponent, {
        width: '1200px',
        data: {budget, generalRemarks: this.generalRemarks, orderId: this.orderId},
      });
      dialogRef.componentInstance.budgetApproved.subscribe(() => {
        this.onBudgetApproved();
      });
    });
  }

  confirmNoReturn(budget: any){
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'alert.information',
        message: 'budget.confirmNoReturn',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirm) {
        this.BudgetsService.updateBudgetStatusToNoResponse(budget.id).subscribe({
          next: () => {
            this.loadBudgets();
            this.loadOrderDetails();
          },
          error: (error) => {
          }
        });
      }
    });
  }
}
